import { FC } from 'react';
import DefaultLayout from '@/layouts/Default/default';
import { motion } from 'framer-motion';

import {
  Heading2,
  ImpactStatement,
  FeatureList,
  Paragraph,
  ProcessStep,
} from '@/components/Blog/BlogComponents';
import { Seo } from '@/components/SEO/Seo';
import { pageMetadata } from '@/config/pageMetadata';

const MachPage: FC = () => {
  const seo = pageMetadata.mach;
  const machCapabilities = [
    {
      title: 'Intelligence Multiplier',
      description: 'Connect human knowledge with machine capability to unlock exponential performance gains across your entire organization.',
    },
    {
      title: 'Custom AI Systems',
      description: 'Deploy company-specific LLMs trained on your data, processes, and expertise for instant access to collective intelligence.',
    },
    {
      title: 'Autonomous Agents',
      description: 'Digital workers that handle complex workflows independently, making decisions and coordinating across systems.',
    },
    {
      title: 'Process Automation',
      description: 'Transform rigid manual processes into fluid, self-optimizing operations that learn and evolve.',
    },
    {
      title: 'Information Velocity',
      description: 'Accelerate how your organization processes and acts on information to outpace competitors.',
    },
    {
      title: 'Exponential Scaling',
      description: 'Scale capabilities without scaling headcount through intelligent systems that multiply team effectiveness.',
    },
  ];

  const machProcess = [
    {
      number: 1,
      title: 'Discovery & Intelligence Mapping',
      description: 'We analyze your current information flows, identify bottlenecks, and map the highest-impact automation opportunities in your organization.',
    },
    {
      number: 2,
      title: 'Foundation Build',
      description: 'Deploy custom LLMs trained on your data, set up initial automation workflows, and integrate with existing systems to create your intelligence core.',
    },
    {
      number: 3,
      title: 'Capability Expansion',
      description: 'Scale intelligent operations by adding autonomous agents, expanding automation workflows, and deepening system integrations.',
    },
    {
      number: 4,
      title: 'Continuous Evolution',
      description: 'Ongoing optimization cycles ensure your systems learn, adapt, and improve performance while expanding capabilities.',
    },
  ];

  return (
    <DefaultLayout>
      <Seo {...seo} />
      <div className="w-full min-h-screen bg-white dark:bg-zinc-950">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mb-16"
          >
            <h1 className="text-4xl md:text-5xl text-zinc-900 dark:text-zinc-100 mb-6">
              Mach
            </h1>
            <p className="text-lg text-zinc-600 dark:text-zinc-400 mb-8">
              10x faster development for tech companies.
            </p>
            
            <Paragraph>
              Every company today has two realities: what their people know, and what their systems know. 
              These exist in separate worlds, creating invisible friction that slows everything down. 
              Mach doesn't just connect these worlds – we create an intelligence multiplier that makes both exponentially more powerful.
            </Paragraph>

            <Paragraph>
              When information flows seamlessly between your people, processes, and systems, you don't just save time – 
              you unlock capabilities that weren't possible before. We're not building better tools; 
              we're transforming how your entire company processes and applies intelligence.
            </Paragraph>
          </motion.div>

          <ImpactStatement content="Companies aren't being disrupted by better products or smarter people – they're being outpaced by organizations that process and act on information faster. The gap between market leaders and everyone else isn't knowledge – it's knowledge flow." />

          <Heading2 
            description="The barriers between human intelligence and machine capability are dissolving, creating unprecedented possibilities for business transformation."
          >
            The New Competitive Advantage
          </Heading2>

          <Paragraph>
            We're living in a moment of unprecedented technological power. The tools reshaping business 
            aren't just incrementally better – they're exponentially more powerful. Custom LLMs that think 
            in your company's language, autonomous agents that handle complex workflows, and intelligent 
            automation that learns and evolves are transforming how businesses operate.
          </Paragraph>

          <Paragraph>
            Traditional approaches to strategy execution, team onboarding, and customer response are being 
            outpaced by organizations that have mastered information velocity. This isn't just about speed – 
            it's about transforming how your entire organization processes and acts on information.
          </Paragraph>

          <Heading2
            description="Comprehensive capabilities that transform how your business operates, thinks, and grows."
          >
            Mach Capabilities
          </Heading2>

          <FeatureList items={machCapabilities} />

          <Heading2
            description="Our proven methodology for transforming your operations into an intelligence multiplier."
          >
            The Mach Process
          </Heading2>

          <div className="mb-16">
            {machProcess.map((step) => (
              <ProcessStep
                key={step.number}
                number={step.number}
                title={step.title}
                description={step.description}
              />
            ))}
          </div>

          <ImpactStatement content="The companies that master this transformation aren't just faster – they're fundamentally more capable. They replace 3-4 full-time employees with intelligent systems that improve quality, consistency, and scale without additional cost." />

          <Heading2
            description="Real results from organizations that have embraced the intelligence multiplier approach."
          >
            Transformation Results
          </Heading2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="bg-zinc-50 dark:bg-zinc-800 p-8 rounded-lg border border-zinc-200 dark:border-zinc-700">
              <h3 className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-2">300%</h3>
              <h4 className="text-lg font-medium mb-2 dark:text-white">Support Capacity</h4>
              <p className="text-zinc-600 dark:text-zinc-300 text-sm">
                Tech scale-up handles more tickets than three full-time agents, with better satisfaction scores.
              </p>
            </div>
            <div className="bg-zinc-50 dark:bg-zinc-800 p-8 rounded-lg border border-zinc-200 dark:border-zinc-700">
              <h3 className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-2">5X</h3>
              <h4 className="text-lg font-medium mb-2 dark:text-white">Processing Volume</h4>
              <p className="text-zinc-600 dark:text-zinc-300 text-sm">
                E-commerce company handling 5X previous volume without adding headcount.
              </p>
            </div>
            <div className="bg-zinc-50 dark:bg-zinc-800 p-8 rounded-lg border border-zinc-200 dark:border-zinc-700">
              <h3 className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-2">75%</h3>
              <h4 className="text-lg font-medium mb-2 dark:text-white">Time Reduction</h4>
              <p className="text-zinc-600 dark:text-zinc-300 text-sm">
                Professional services firm: what required four analysts now happens automatically.
              </p>
            </div>
          </div>

          <Heading2
            description="Ready to transform your operations into an intelligence multiplier?"
          >
            Start Your Mach Transformation
          </Heading2>

          <Paragraph>
            The gap between average and exceptional business performance isn't about hiring more people – 
            it's about building intelligent systems that multiply what your current team can accomplish. 
            Mach represents the future of business operations: faster, smarter, and exponentially more capable.
          </Paragraph>

          <div className="bg-zinc-50 dark:bg-zinc-900 p-8 rounded-lg border border-zinc-200 dark:border-zinc-700 text-center">
            <h3 className="text-2xl font-bold mb-4 dark:text-white">Ready to Experience Mach Speed?</h3>
            <p className="text-zinc-600 dark:text-zinc-300 mb-6">
              Let's explore how we can transform your operations into an intelligence multiplier that delivers 10x faster development.
            </p>
            <div className="space-x-4">
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium transition-colors">
                Schedule Strategy Session
              </button>
              <button className="border border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 px-8 py-3 rounded-lg font-medium transition-colors">
                Learn About Our Process
              </button>
            </div>
          </div>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default MachPage;
